/**
 * Types and utilities for dealing with email domain verification
 */

import { Team } from "../team";

/**
 * Status of DKIM verification for a domain
 */
export enum DkimStatus {
  PENDING = "pending",
  VERIFIED = "verified",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

/**
 * Status of Mail From domain verification
 */
export enum MailFromDomainStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

/**
 * Email domain settings for a team
 */
export interface EmailDomainSettings {
  enabled: boolean;
  domain: string;
  mailFromDomain?: string | null;
  mailFromDomainStatus?: MailFromDomainStatus | null;
  fromName?: string | null;
  fromEmail?: string | null;
  dkimSelector: string;
  dkimStatus: DkimStatus;
  configurationSets?: { transactional: string; marketing: string } | null;
  redoMailEnabled?: boolean | null;
  /**
   * Used for domain warming calculations, but needs to exist before warming begins
   */
  estimatedSendsPerWeek?: number | null;
  warmingEnabled?: boolean | null;
  warming?: DomainWarmingSettings | null;
}

export interface DomainWarmingSettings {
  from: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  to: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  currentTier: DomainWarmingEntry;
  history: DomainWarmingEntry[];
  startedAt: Date;
  finishedAt?: Date | null;
}

export interface DomainWarmingEntry {
  tierIndex: number;
  enteredAt: Date;
}

/**
 * Checks if an email domain is verified
 *
 * @param emailDomainSettings The email domain settings to check
 * @returns A type guard that ensures the domain is both enabled and verified
 */
export function isVerifiedEmailDomain(
  emailDomainSettings?: EmailDomainSettings,
): emailDomainSettings is EmailDomainSettings & {
  enabled: true;
  dkimStatus: DkimStatus.VERIFIED;
} {
  return (
    !!emailDomainSettings &&
    emailDomainSettings.enabled &&
    emailDomainSettings.dkimStatus === DkimStatus.VERIFIED
  );
}

export const WARMING_DOMAINS = [
  "shared1.getredomail.com",
  "shared2.getredomail.com",
  "shared3.getredomail.com",
  "shared4.getredomail.com",
];

export const TRANSACTIONAL_EMAIL_DOMAIN = "notify.getredomail.com";
export const TRANSACTIONAL_EMAIL_CONFIGURATION_SET =
  "notify_getredomail_com-txnl-conf-set";
export const MARKETING_EMAIL_DOMAIN = "shared.getredomail.com";
export const MARKETING_EMAIL_CONFIGURATION_SET =
  "shared_getredomail_com-mktg-conf-set";
export const WARMING_EMAIL_CONFIGURATION_SET =
  "warming_getredomail_com-mktg-conf-set";

export function getFromEmailConfigIgnoringWarming(
  team: Team,
  isTransactional: boolean,
): { name: string; email: string } {
  const emailDomainSettings = team?.settings?.emailDomain ?? undefined;
  if (isVerifiedEmailDomain(emailDomainSettings)) {
    return {
      name: emailDomainSettings.fromName || team.name || "",
      email: `${emailDomainSettings.fromEmail || "no-reply"}@${emailDomainSettings.domain}`,
    };
  } else if (emailDomainSettings && emailDomainSettings.redoMailEnabled) {
    const emailDomain = isTransactional
      ? TRANSACTIONAL_EMAIL_DOMAIN
      : MARKETING_EMAIL_DOMAIN;
    return { name: team.name ?? "Redo", email: `no-reply@${emailDomain}` };
  } else {
    return { name: team.name ?? "Redo", email: "no-reply@getredo.com" };
  }
}
